/**
 * Dynamic calculation engine for CardFlow diagrams.
 * Supports SUM, AVG, COUNT, MIN, MAX, PERCENT formulas
 * that react to edge connections in real-time.
 */

export function parseAmount(str) {
  if (typeof str === "number") return str;
  if (!str) return 0;
  const cleaned = str
    .toString()
    .replace(/\s/g, "")
    .replace(/EUR|€|\$/gi, "")
    .replace(",", ".");
  return parseFloat(cleaned) || 0;
}

export function formatAmount(num, suffix = "") {
  if (num === null || num === undefined || isNaN(num)) return "0";
  const formatted = new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: 2,
  }).format(num);
  return suffix ? `${formatted} ${suffix}` : formatted;
}

function getConnectedValues(node, allNodes, edges, direction) {
  let connectedNodes;
  if (direction === "out") {
    const outEdges = edges.filter((e) => e.source === node.id);
    connectedNodes = outEdges
      .map((e) => allNodes.find((n) => n.id === e.target))
      .filter(Boolean);
  } else {
    const inEdges = edges.filter((e) => e.target === node.id);
    connectedNodes = inEdges
      .map((e) => allNodes.find((n) => n.id === e.source))
      .filter(Boolean);
  }
  return {
    nodes: connectedNodes,
    values: connectedNodes.map((n) =>
      parseAmount(n.data._calcResult ?? n.data.amount)
    ),
  };
}

function computeFormula(node, allNodes, edges) {
  const f = node.data.formula;
  if (!f || !f.type) return null;

  const dir = f.direction || "in";
  const { nodes: connected, values } = getConnectedValues(
    node,
    allNodes,
    edges,
    dir
  );

  switch (f.type) {
    case "SUM":
      return values.reduce((a, b) => a + b, 0);
    case "AVG":
      return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
    case "COUNT":
      return connected.length;
    case "MIN":
      return values.length ? Math.min(...values) : 0;
    case "MAX":
      return values.length ? Math.max(...values) : 0;
    case "PERCENT": {
      const parentNodes = getConnectedValues(node, allNodes, edges, "in").nodes;
      const parent = parentNodes[0];
      if (!parent) return 0;
      const parentVal = parseAmount(parent.data._calcResult ?? parent.data.amount);
      return (parentVal * (f.percent || 0)) / 100;
    }
    case "MULTIPLY": {
      return values.length ? values.reduce((a, b) => a * b, 1) : 0;
    }
    default:
      return null;
  }
}

/**
 * Recalculate all nodes with formulas.
 * Uses 3 passes to handle formula chains (A -> B -> C).
 */
export function recalculateAll(nodes, edges) {
  let working = nodes.map((n) => ({
    ...n,
    data: { ...n.data, _calcResult: undefined, calculatedAmount: undefined },
  }));

  for (let pass = 0; pass < 4; pass++) {
    working = working.map((node) => {
      const result = computeFormula(node, working, edges);
      if (result !== null) {
        const suffix = node.data.formula?.suffix || "";
        return {
          ...node,
          data: {
            ...node.data,
            _calcResult: result,
            calculatedAmount: formatAmount(result, suffix),
          },
        };
      }
      return node;
    });
  }

  // Clean up _calcResult
  return working.map((n) => {
    const { _calcResult, ...rest } = n.data;
    return { ...n, data: rest };
  });
}

export const FORMULA_TYPES = [
  { type: "SUM", label: "Somme", icon: "Σ", description: "Total des connectés" },
  { type: "AVG", label: "Moyenne", icon: "μ", description: "Moyenne des connectés" },
  { type: "COUNT", label: "Comptage", icon: "N", description: "Nombre de connexions" },
  { type: "MIN", label: "Minimum", icon: "↓", description: "Valeur la plus basse" },
  { type: "MAX", label: "Maximum", icon: "↑", description: "Valeur la plus haute" },
  { type: "PERCENT", label: "Pourcentage", icon: "%", description: "% de la valeur parente" },
];
